import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FiEdit3, FiTrendingUp } from "react-icons/fi";
import axios from "axios";
import AuthContext from "../../../../../auth/auth-context";
import { numberFormat } from "../../../../../common/helper";
import InvestecLogo from "../../../../../assets/Banks/investec-logo-cropped.png";
import CapitecLogo from "../../../../../assets/Banks/capitec-logo-cropped.png";

const Card = styled.div`
  position: relative;
  background: linear-gradient(180deg, #f3faf5 0%, #ffffff 48%);
  border: 1px solid #b7dfc3;
  border-radius: 12px;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 8px 20px rgba(25, 135, 84, 0.08);
  overflow: hidden;
  margin-bottom: 16px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, #198754 0%, #146c43 100%);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 14px 20px;
  background: rgba(25, 135, 84, 0.06);
  border-bottom: 1px solid #cfead8;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const MethodBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.85rem;
  color: #0f5132;
  background: #ffffff;
  border: 1px solid #a3d7b5;
  padding: 6px 10px;
  border-radius: 999px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #0f5132;
  background: #d1e7dd;
  border: 1px solid #a3d7b5;
  padding: 6px 10px;
  border-radius: 999px;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
`;

const Body = styled.div`
  padding: 14px 16px 16px 20px;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 12px;
  color: #146c43;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
`;

const Row = styled.div`
  display: contents;
`;

const Label = styled.span`
  color: #6c757d;
  padding: 6px 0;
`;

const Value = styled.span`
  color: #212529;
  font-weight: 600;
  padding: 6px 0;
`;

const Muted = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const EXECUTION_SOURCE = {
  CAPITEC: "capitec",
  INVESTEC: "investec",
  MANUAL: "manual",
};

const isCapitecBank = (bankName) =>
  bankName === "Capitec Bank" || bankName === "Capitec Business";

const isInvestecBank = (bankName) => bankName === "Investec Bank";

const formatRate = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(4);
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatAmount = (currencySymbol, amount) => {
  if (amount == null || amount === "") return "—";
  return `${currencySymbol || ""}${numberFormat(amount)}`;
};

function ExecutedTradeSummary({ trade }) {
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null);
  const [execution, setExecution] = useState(null);

  const headers = useMemo(
    () => ({
      "Acting-as": "dealer",
      Authorization: `JWT ${authCtx?.token}`,
    }),
    [authCtx?.token],
  );

  const bankName = trade?.bank?.display_name;
  const status = trade?.status;
  const shouldLoad =
    status === "COMPLETED" || status === "BOOKED";

  useEffect(() => {
    let cancelled = false;

    async function loadExecutedTrade() {
      if (!shouldLoad || !trade?.id) {
        setSource(null);
        setExecution(null);
        return;
      }

      setLoading(true);

      try {
        if (isCapitecBank(bankName)) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/capitec/fx/quote/book-responses-by-local-trade/`,
              {
                headers,
                params: { local_trade_id: trade.id },
              },
            );
            const activeOrLatest = [...(response.data || [])]
              .filter((item) => item.status === "ACTIVE" || !item.status)
              .sort(
                (a, b) =>
                  new Date(b.request_date_time).getTime() -
                  new Date(a.request_date_time).getTime(),
              );

            const latest =
              activeOrLatest[0] ||
              [...(response.data || [])].sort(
                (a, b) =>
                  new Date(b.request_date_time).getTime() -
                  new Date(a.request_date_time).getTime(),
              )[0];

            if (!cancelled && latest) {
              setSource(EXECUTION_SOURCE.CAPITEC);
              setExecution(latest);
              setLoading(false);
              return;
            }
          } catch (error) {
            // No Capitec execution — fall through to manual
            console.log("No Capitec executed trade found", error);
          }
        }

        if (isInvestecBank(bankName)) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_SERVER_URL}/broker-portal/trade/investec/fx/trade/by-trade-id/`,
              {
                headers,
                params: { associated_local_trade_id: trade.id },
              },
            );
            const latest = [...(response.data || [])].sort(
              (a, b) =>
                new Date(b.request_date_time).getTime() -
                new Date(a.request_date_time).getTime(),
            )[0];

            if (!cancelled && latest) {
              setSource(EXECUTION_SOURCE.INVESTEC);
              setExecution(latest);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.log("No Investec executed trade found", error);
          }
        }

        if (!cancelled) {
          setSource(EXECUTION_SOURCE.MANUAL);
          setExecution(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadExecutedTrade();

    return () => {
      cancelled = true;
    };
  }, [shouldLoad, trade?.id, bankName, headers]);

  const pair = useMemo(() => {
    const left =
      trade?.trade_currency?.alpha_3 || trade?.trade_currency?.symbol;
    const right = trade?.fx_currency?.alpha_3 || trade?.fx_currency?.symbol;
    if (left || right) return `${left || "—"}/${right || "—"}`;
    return "—/—";
  }, [trade]);

  if (!shouldLoad) return null;

  const renderMethod = () => {
    if (loading || !source) {
      return (
        <Left>
          <MethodBadge>Loading…</MethodBadge>
        </Left>
      );
    }
    if (source === EXECUTION_SOURCE.CAPITEC) {
      return (
        <Left>
          <Logo src={CapitecLogo} alt="Capitec" />
          <MethodBadge>Capitec</MethodBadge>
        </Left>
      );
    }
    if (source === EXECUTION_SOURCE.INVESTEC) {
      return (
        <Left>
          <Logo src={InvestecLogo} alt="Investec" />
          <MethodBadge>Investec</MethodBadge>
        </Left>
      );
    }
    return (
      <Left>
        <MethodBadge>
          <FiEdit3 size={14} /> Manual
        </MethodBadge>
      </Left>
    );
  };

  const renderDetails = () => {
    if (loading || !source) {
      return <Muted>Loading executed trade details…</Muted>;
    }

    if (source === EXECUTION_SOURCE.CAPITEC && execution) {
      return (
        <Grid>
          <Row>
            <Label>Deal Reference</Label>
            <Value>{execution.deal_transaction_id || "—"}</Value>
          </Row>
          <Row>
            <Label>Status</Label>
            <Value>{execution.status || "—"}</Value>
          </Row>
          <Row>
            <Label>Deal Rate</Label>
            <Value>{formatRate(execution.deal_rate)}</Value>
          </Row>
          <Row>
            <Label>Quote Rate</Label>
            <Value>{formatRate(execution.quote_rate)}</Value>
          </Row>
          <Row>
            <Label>Currency Pair</Label>
            <Value>{pair}</Value>
          </Row>
          <Row>
            <Label>Amount</Label>
            <Value>
              {formatAmount(
                trade?.trade_currency?.symbol,
                trade?.trade_amount,
              )}
            </Value>
          </Row>
          <Row>
            <Label>Executed At</Label>
            <Value>{formatDateTime(execution.request_date_time)}</Value>
          </Row>
          <Row>
            <Label>Transaction ID</Label>
            <Value>{execution.transaction_id || "—"}</Value>
          </Row>
        </Grid>
      );
    }

    if (source === EXECUTION_SOURCE.INVESTEC && execution) {
      return (
        <Grid>
          <Row>
            <Label>Trade Reference</Label>
            <Value>{execution.trade_reference || "—"}</Value>
          </Row>
          <Row>
            <Label>Status</Label>
            <Value>{execution.status || "—"}</Value>
          </Row>
          <Row>
            <Label>Executed Rate</Label>
            <Value>{formatRate(execution.counterparty_client_rate)}</Value>
          </Row>
          <Row>
            <Label>Account Number</Label>
            <Value>{execution.account_number || "—"}</Value>
          </Row>
          <Row>
            <Label>Currency Pair</Label>
            <Value>{pair}</Value>
          </Row>
          <Row>
            <Label>Amount</Label>
            <Value>
              {formatAmount(
                trade?.trade_currency?.symbol,
                trade?.trade_amount,
              )}
            </Value>
          </Row>
          <Row>
            <Label>Executed At</Label>
            <Value>{formatDateTime(execution.request_date_time)}</Value>
          </Row>
        </Grid>
      );
    }

    return (
      <Grid>
        <Row>
          <Label>Reference</Label>
          <Value>{trade?.trade_reference_number || "—"}</Value>
        </Row>
        <Row>
          <Label>Executed Rate</Label>
          <Value>
            {formatRate(trade?.exchange_rate ?? trade?.trade_rate)}
          </Value>
        </Row>
        <Row>
          <Label>Currency Pair</Label>
          <Value>{pair}</Value>
        </Row>
        <Row>
          <Label>Trade Amount</Label>
          <Value>
            {formatAmount(trade?.trade_currency?.symbol, trade?.trade_amount)}
          </Value>
        </Row>
        <Row>
          <Label>FX Amount</Label>
          <Value>
            {formatAmount(trade?.fx_currency?.symbol, trade?.fx_amount)}
          </Value>
        </Row>
        <Row>
          <Label>Trade Date</Label>
          <Value>{formatDateTime(trade?.trade_date)}</Value>
        </Row>
        <Row>
          <Label>Bank</Label>
          <Value>{bankName || "—"}</Value>
        </Row>
      </Grid>
    );
  };

  return (
    <Card>
      <Header>
        {renderMethod()}
        <StatusPill>
          <FiTrendingUp /> Executed Trade
        </StatusPill>
      </Header>
      <Body>
        <SectionTitle>Execution Details</SectionTitle>
        {renderDetails()}
      </Body>
    </Card>
  );
}

ExecutedTradeSummary.propTypes = {
  trade: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    trade_reference_number: PropTypes.string,
    exchange_rate: PropTypes.number,
    trade_rate: PropTypes.number,
    trade_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    fx_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trade_date: PropTypes.string,
    bank: PropTypes.shape({
      display_name: PropTypes.string,
    }),
    trade_currency: PropTypes.shape({
      alpha_3: PropTypes.string,
      symbol: PropTypes.string,
    }),
    fx_currency: PropTypes.shape({
      alpha_3: PropTypes.string,
      symbol: PropTypes.string,
    }),
  }),
};

export default ExecutedTradeSummary;
